import { motion } from 'framer-motion'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import styles from './Quickstart.module.css'

interface CodeLine {
  text: string
  comment?: string
}

const steps: Array<{
  number: number
  title: string
  content?: React.ReactNode
  code?: CodeLine[]
}> = [
  {
    number: 1,
    title: 'Install Dependencies',
    content: <p>Make sure you have <a href="https://www.docker.com/get-started/" target="_blank" rel="noopener noreferrer">Docker</a> installed and an OpenAI API key.</p>,
    code: [
      { text: '# Clone the repo' },
      { text: 'git clone https://github.com/luminox-mem/luminox.git' },
    ],
  },
  {
    number: 2,
    title: 'Start the Backend',
    code: [
      { text: 'cd luminox' },
      { text: 'docker compose up -d' },
    ],
  },
  {
    number: 3,
    title: 'Access Your Instance',
    content: (
      <>
        <p><strong>API:</strong> http://localhost:8029/api/v1</p>
        <p><strong>Dashboard:</strong> http://localhost:3000</p>
      </>
    ),
  },
  {
    number: 4,
    title: 'Install the SDK',
    content: <p>TypeScript is available on npm. Python is installed from the cloned repo.</p>,
    code: [
      { text: 'npm install luminox-sdk', comment: '# TypeScript (from npm)' },
      { text: '# Python: from the luminox directory' },
      { text: 'cd src/client/luminox-py && pip install -e .' },
    ],
  },
]

export default function Quickstart() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>()

  return (
    <section id="quickstart" className={styles.quickstart} ref={ref}>
      <motion.div
        className="section-header"
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
      >
        <span className="section-tag">Get Started</span>
        <h2 className="section-title">Self-Host in Minutes</h2>
      </motion.div>

      <motion.div
        className={styles.content}
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.3 }}
      >
        {steps.map((step, index) => (
          <motion.div
            key={step.number}
            className={styles.step}
            initial={{ opacity: 0, x: -20 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
          >
            <motion.div
              className={styles.stepNumber}
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              {step.number}
            </motion.div>
            <div className={styles.stepContent}>
              <h4>{step.title}</h4>
              {step.content}
              {step.code && (
                <div className={styles.codeBlock}>
                  {step.code.map((line, i) => (
                    <code key={i}>
                      {line.text}
                      {line.comment && <span className={styles.comment}> {line.comment}</span>}
                    </code>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  )
}
